/**
 * LO QUE REQUIERE TU ATENCIÓN, Y SÓLO UNA COSA.
 *
 * Cesar: «pones un comentario al inicio de que si requiere mi atención va en
 * rojo, pero no veo nada en rojo. ¿Eso sigue funcionando?».
 *
 * No funcionaba: nunca llegó a existir. Marty abre el dashboard diciendo «así va
 * el mes; si algo requiere tu atención, va en rojo — y sólo una cosa a la vez»
 * (`lib/marty.ts`), y lo único rojo de toda la pantalla era el EBITDA cuando
 * salía negativo. O sea: una promesa sin nada detrás, que es peor que no
 * hacerla — quien la lee y no ve nada rojo concluye que todo está bien.
 *
 * ===========================================================================
 * UNA. NO UNA LISTA.
 * ===========================================================================
 *
 * La promesa dice «sólo una cosa a la vez» y se cumple literalmente: esta
 * función devuelve UNA señal o ninguna. No es una limitación técnica, es lo que
 * la hace servir para algo.
 *
 * Un panel de alertas con seis avisos en ámbar es un panel que nadie mira a la
 * semana de tenerlo: si todo está marcado, nada está marcado. Y en un producto
 * contable eso tiene una consecuencia concreta — el aviso de que la caja se va a
 * negativo acaba con el mismo peso visual que el de tres comprobantes sin
 * clasificar, así que se aprende a ignorar los dos.
 *
 * ===========================================================================
 * EL ORDEN ES LA DECISIÓN, Y ES CONTABLE
 * ===========================================================================
 *
 * No están ordenadas por lo llamativas que son sino por lo que cuesta no
 * enterarse:
 *
 *  1. LA CAJA SE VA A NEGATIVO. Es la única que impide pagar la planilla. Va
 *     primera aunque el EBITDA sea estupendo: se quiebra por caja, no por
 *     resultado.
 *  2. EL EBITDA ES NEGATIVO. El negocio no cubre su propia operación.
 *  3. HAY DOCUMENTOS ATASCADOS. Lo que no entró al libro no está en ninguna de
 *     las dos cifras de arriba — así que un EBITDA sano con cuarenta facturas de
 *     compra sin procesar es un EBITDA que todavía no sabe lo que cuesta.
 *  4. LO QUE DIGA EL BACKEND. `ingest.alerta` la produce el servidor sobre el
 *     estado real de la ingesta; va última porque es la más genérica, no porque
 *     importe menos.
 *
 * ===========================================================================
 * SIN SEÑAL SE DICE QUE NO HAY SEÑAL
 * ===========================================================================
 *
 * Devolver `null` no es lo mismo que no pintar nada. La pantalla enseña «no hay
 * nada que necesite tu atención», y eso es información: distingue «lo miré y
 * está bien» de «esto no funciona». Es exactamente la duda que tuvo Cesar.
 */

export interface SenalDeAtencion {
  /** Estable, para pruebas y para silenciar. Nunca se muestra. */
  clave: "caja_negativa" | "ebitda_negativo" | "documentos_atascados" | "ingesta"
  /**
   * Lo que se lee, en la voz de Marty: primera persona, una frase, sin rodeos.
   * Es la CLAVE de traducción además del texto — ver `lib/i18n.tsx`.
   */
  titulo: string
  /** El dato que lo sostiene. Una afirmación sin cifra no se puede comprobar. */
  detalle: string
  /** Dónde se resuelve. Sin esto, el aviso es un reproche. */
  a: string
  etiquetaAccion: string
}

export interface EntradaDeAtencion {
  /**
   * La caja ACUMULADA, bucket a bucket, tal como se dibuja en el gráfico. Es el
   * saldo del ejercicio, no el bancario: cruzar por debajo de cero significa que
   * el período se financia con otra cosa.
   */
  cajaAcumulada: number[]
  /** Las etiquetas de esos buckets, para poder nombrar CUÁNDO pasa. */
  etiquetas: string[]
  /** EBITDA del período que se está mirando. */
  ebitda: number
  /** Ingresos del mismo período, para no gritar por un mes sin actividad. */
  ingresos: number
  /** Comprobantes que entraron y no llegaron al libro. */
  sinNormalizar: number
  /** La alerta que produce el backend sobre la ingesta, si hay. */
  alertaIngesta: string | null
}

export function loQueRequiereAtencion(e: EntradaDeAtencion): SenalDeAtencion | null {
  // 1 · La caja bajo cero. Se busca el PRIMER bucket que cruza, no el peor:
  // lo accionable es cuándo empieza el problema, no cuánto llega a doler.
  const i = e.cajaAcumulada.findIndex((v) => v < 0)
  if (i >= 0) {
    return {
      clave: "caja_negativa",
      titulo: "Tu caja se va por debajo de cero",
      detalle: `Pasa en ${e.etiquetas[i] ?? "el período"}. El ejercicio deja de financiarse solo: a partir de ahí hace falta otra cosa —un aporte, una línea o cobrar antes.`,
      a: "/finanzas/caja",
      etiquetaAccion: "Ver el flujo de caja",
    }
  }

  /*
    2 · EBITDA negativo, pero SÓLO si hubo ingresos.

    Sin ingresos el EBITDA es negativo por definición —son los gastos con signo
    menos— y eso pasa en un mes que aún no se ha cargado, en un período futuro y
    en una sociedad recién creada. Avisar ahí es avisar de que el calendario
    existe, y es la forma más rápida de que el aviso pierda todo su crédito.
  */
  if (e.ingresos > 0 && e.ebitda < 0) {
    return {
      clave: "ebitda_negativo",
      titulo: "El período cierra con EBITDA negativo",
      detalle: `La operación cuesta más de lo que ingresa. Con ${fmt(e.ingresos)} de ingresos, el resultado queda en ${fmt(e.ebitda)}.`,
      a: "/finanzas/pnl",
      etiquetaAccion: "Abrir el estado de resultados",
    }
  }

  // 3 · Documentos que entraron y no llegaron al libro.
  if (e.sinNormalizar > 0) {
    return {
      clave: "documentos_atascados",
      titulo: "Tengo comprobantes que no llegaron al libro",
      detalle: `Son ${e.sinNormalizar}. Mientras estén ahí, no cuentan en ninguna de las cifras de esta pantalla.`,
      a: "/contabilidad/revision",
      etiquetaAccion: "Ir a la revisión",
    }
  }

  // 4 · Lo que diga el backend sobre la ingesta.
  if (e.alertaIngesta) {
    return {
      clave: "ingesta",
      titulo: "Hay algo con la entrada de documentos",
      detalle: e.alertaIngesta,
      a: "/contabilidad/revision",
      etiquetaAccion: "Ir a la revisión",
    }
  }

  return null
}

/**
 * Importe corto para el detalle de una señal.
 *
 * NO usa `formatAmount` a propósito: aquél sigue a la moneda y al locale de la
 * sociedad, y esto es una frase, no una celda de una tabla. Lo que hace falta
 * aquí es una magnitud legible dentro de un párrafo — «41.000» y no
 * «S/ 41.000,00», que dentro de una oración se lee como un tropiezo.
 */
function fmt(v: number): string {
  return new Intl.NumberFormat("es", { maximumFractionDigits: 0 }).format(v)
}
